'use client';

/**
 * Auth Layout Client Component
 * 
 * Client-side wrapper for auth pages with a beautiful split-screen design.
 * Features a stunning property showcase on the left and auth form on the right.
 */

import { FormatPrice } from '@/components/ui/FormatPrice';
import { useTenantOptional } from '@/contexts/TenantContext';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import { Building2, MapPin, ArrowRight, ArrowLeft } from 'lucide-react';

/**
 * Featured property data structure passed from server
 */
export interface FeaturedPropertyData {
  id: string;
  title: string;
  location: string;
  price: number | null;
  currency: string;
  bedrooms: number | null;
  bathrooms: number | null;
  area: string | null;
  imageUrl: string | null;
  slug: string;
}

interface AuthLayoutClientProps {
  children: ReactNode;
  featuredProperty?: FeaturedPropertyData | null;
}

// Fallback property when no featured property is available
const fallbackProperty = {
  imageUrl: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2075&auto=format&fit=crop',
  title: 'Discover Luxury Properties',
  location: 'Mauritius',
};

export function AuthLayoutClient({ children, featuredProperty }: AuthLayoutClientProps) {
  const tenant = useTenantOptional();
  const pathname = usePathname();
  
  const brandName = tenant?.branding?.brandName || 'Allys';
  const logo = tenant?.branding?.logo;
  
  // Extract locale from pathname (e.g., /en/login → en)
  const localeMatch = pathname.match(/^\/([a-z]{2})\//);
  const locale = localeMatch?.[1] || tenant?.locale || 'en';
  const basePath = `/${locale}`;
  
  // Use featured property from API or fallback
  const displayProperty = featuredProperty || null;
  const propertyImage = displayProperty?.imageUrl || fallbackProperty.imageUrl;
  const propertyTitle = displayProperty?.title || fallbackProperty.title;
  const propertyLocation = displayProperty?.location || fallbackProperty.location;

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Property Showcase */}
      <div className="hidden lg:flex lg:w-[55%] xl:w-[60%] relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src={propertyImage}
            alt={propertyTitle}
            fill
            className="object-cover"
            priority
            sizes="60vw"
          />
          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          {/* Top - Logo */}
          <div>
            <Link href={basePath} className="inline-flex items-center gap-3 group">
              {logo ? (
                  <Image
                    src={logo}
                    alt={brandName}
                  width={160}
                  height={44}
                  className="h-11 w-auto object-contain brightness-0 invert"
                    priority
                  />
              ) : (
                <span className="text-2xl font-bold text-white tracking-tight">
                  {brandName}
                </span>
              )}
            </Link>
          </div>

          {/* Middle - Tagline */}
          <div className="flex-1 flex items-center">
            <div className="max-w-lg">
              <h1 
                className="text-4xl xl:text-5xl font-light text-white leading-tight tracking-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Discover your
                <span className="block font-semibold italic mt-1">dream home</span>
                <span className="block mt-1">in paradise</span>
              </h1>
              <p className="mt-6 text-lg text-white/70 font-light leading-relaxed max-w-md">
                Access exclusive listings, save your favorites, and connect with our expert agents to find your perfect property.
              </p>
            </div>
          </div>

          {/* Bottom - Featured Property Card */}
          {displayProperty ? (
            <div 
              className="backdrop-blur-xl bg-white/10 rounded-2xl p-6 border border-white/20 max-w-md"
              style={{
                animation: 'slideUpFade 0.6s ease-out 0.3s both',
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-1">
                    Featured Property
                  </p>
                  <h3 className="text-xl font-semibold text-white">
                    {propertyTitle}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-1 text-white/60">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="text-sm">{propertyLocation}</span>
                  </div>
                </div>
              </div>
              
              {/* Property Details */}
              {(displayProperty.bedrooms || displayProperty.bathrooms || displayProperty.area) && (
                <div className="flex items-center gap-4 py-3 border-t border-white/10">
                  {displayProperty.bedrooms && (
                    <>
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-white/50" />
                        <span className="text-sm text-white/80">{displayProperty.bedrooms} bed</span>
                      </div>
                      <div className="w-px h-4 bg-white/20" />
                    </>
                  )}
                  {displayProperty.bathrooms && (
                    <>
                      <span className="text-sm text-white/80">{displayProperty.bathrooms} bath</span>
                      {displayProperty.area && <div className="w-px h-4 bg-white/20" />}
                    </>
                  )}
                  {displayProperty.area && (
                    <span className="text-sm text-white/80">{displayProperty.area}</span>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-white/10">
                <div>
                  {displayProperty.price && (
                    <FormatPrice
                      amount={displayProperty.price}
                      currency={displayProperty.currency}
                      className="text-2xl font-bold text-white"
                      size="xl"
                    />
                  )}
                </div>
                <Link 
                  href={`${basePath}/properties/${displayProperty.slug}`}
                  className="flex items-center gap-2 text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors group"
                >
                  View Property
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          ) : (
            /* Fallback CTA when no featured property */
            <div 
              className="backdrop-blur-xl bg-white/10 rounded-2xl p-6 border border-white/20 max-w-md"
              style={{
                animation: 'slideUpFade 0.6s ease-out 0.3s both',
              }}
            >
              <p className="text-white/80 mb-4">
                Explore our collection of exclusive properties in Mauritius.
              </p>
              <Link 
                href={`${basePath}/properties`}
                className="flex items-center gap-2 text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors group"
              >
                Browse All Properties
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex flex-col min-h-screen bg-stone-50">
        {/* Mobile Logo Header */}
        <div className="lg:hidden p-6 border-b border-stone-200/60 flex items-center justify-between">
          <Link href={basePath} className="inline-flex items-center gap-3">
            {logo ? (
              <Image
                src={logo}
                alt={brandName}
                width={140}
                height={40}
                className="h-10 w-auto object-contain"
                priority
              />
            ) : (
              <span className="text-xl font-bold text-stone-900 tracking-tight">
                {brandName}
              </span>
              )}
            </Link>
          <Link
            href={basePath}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to site</span>
          </Link>
          </div>

        {/* Back to site - Desktop */}
        <div className="hidden lg:block pt-6 px-12">
          <Link
            href={basePath}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-400 hover:text-stone-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to site</span>
          </Link>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-10 lg:p-12">
          <div className="w-full max-w-[440px]">
            {/* Auth Card */}
          <div 
              className="bg-white rounded-2xl p-8 sm:p-10"
            style={{
                boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04), 0 16px 40px rgba(0,0,0,0.04)',
            }}
          >
            {children}
          </div>

          {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-sm text-stone-400">
              © {new Date().getFullYear()} {brandName}. All rights reserved.
            </p>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="hidden lg:block absolute top-20 right-20 w-64 h-64 bg-amber-100/40 rounded-full blur-3xl pointer-events-none" />
        <div className="hidden lg:block absolute bottom-20 right-40 w-48 h-48 bg-stone-200/50 rounded-full blur-3xl pointer-events-none" />
      </div>

      {/* Global Styles */}
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&display=swap');
        
        @keyframes slideUpFade {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default AuthLayoutClient;
